import { Pencil } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { SetupAnalysisResult } from '@/types/agent/setup';
import { GenieCategories } from "@/types/shared/categories";
import { ReviewSectionCard } from "./ReviewSectionCard";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface ConfirmationSummarySectionProps {
  analysis: SetupAnalysisResult;
  onEditStep: (stepId: string) => void;
  selectedKnowledgeBase?: SetupAnalysisResult['knowledgeBase'];
}

export const ConfirmationSummarySection = ({ analysis, onEditStep, selectedKnowledgeBase }: ConfirmationSummarySectionProps) => {
  const knowledgeBase = selectedKnowledgeBase ?? analysis.knowledgeBase;
  const category = GenieCategories.find((cat) => cat.id === analysis.category) || GenieCategories.find((cat) => cat.id === 'other')!;

  const SummaryCard = ({
    title,
    genieIcon,
    stepId,
    children
  }: {
    title: string;
    genieIcon: GenieIconName;
    stepId: string;
    children: React.ReactNode;
  }) => (
    <Card className="p-5 bg-white border-2 border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(78, 156, 255, 0.1)' }}>
            <GenieIcon name={genieIcon} size={24} color="#4E9CFF" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEditStep(stepId)}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <Pencil className="w-4 h-4 mr-1" />
          Edit
        </Button>
      </div>
      <div className="space-y-2 text-sm text-gray-700">
        {children}
      </div>
    </Card>
  );

  const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => {
    if (!value) return null;
    return (
      <div className="flex items-start">
        <span className="font-medium text-gray-900 min-w-[100px]">{label}:</span>
        <span className="text-gray-700 flex-1">{value}</span>
      </div>
    );
  };

  return (
    <ReviewSectionCard
      title="Review & Create"
      subtitle="Verify all settings before creating your genie"
      genieIcon="chat-check"
      categoryColor={category.color}
    >
      <div className="space-y-6">
        {/* Summary message */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-sm text-gray-700 leading-relaxed">
            Review your genie configuration below. You can edit any section by clicking the "Edit" button.
            When everything looks good, click "Create Genie" at the bottom.
          </p>
        </div>

        {/* Identity Summary */}
        <SummaryCard title="Identity & Voice" genieIcon="brain" stepId="identity">
          <InfoRow label="Name" value={analysis.genieName} />
          <InfoRow label="Category" value={category.label} />
          <InfoRow label="Use Case" value={analysis.useCase} />
          <InfoRow label="Language" value={analysis.language?.toUpperCase() || 'EN'} />
          {analysis.voiceId && <InfoRow label="Voice" value="Custom voice selected" />}
        </SummaryCard>

        {/* Conversation Summary */}
        <SummaryCard title="Conversation Flow" genieIcon="settings" stepId="conversation">
          <div>
            <span className="font-medium text-gray-900">First Message:</span>
            <p className="text-gray-700 mt-1 italic">"{analysis.firstMessage}"</p>
          </div>
          {analysis.personalityTraits && analysis.personalityTraits.length > 0 && (
            <div>
              <span className="font-medium text-gray-900">Personality:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {analysis.personalityTraits.map((trait) => (
                  <Badge key={trait} variant="outline" className="text-xs">
                    {trait}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {analysis.systemPrompt && (
            <InfoRow
              label="System Prompt"
              value={`${analysis.systemPrompt.substring(0, 100)}${analysis.systemPrompt.length > 100 ? '...' : ''}`}
            />
          )}
        </SummaryCard>

        {/* Goals Summary */}
        <SummaryCard title="Goals & Outcomes" genieIcon="lightbulb" stepId="insights">
          <InfoRow label="Collection Goal" value={analysis.goal || 'Not specified'} />
          <div>
            <span className="font-medium text-gray-900">Reporting Mode:</span>
            <span className="text-gray-700 ml-2">
              {(() => {
                const mode = analysis.reportMode ?? (analysis.sendConversationReports ? 'every_conversation' : 'off');
                if (mode === 'every_conversation') return 'Every conversation';
                if (mode === 'digest') return `Digest${analysis.reportDigestFrequency ? ` (${analysis.reportDigestFrequency})` : ''}`;
                if (mode === 'escalation_only') return 'Only when attention needed';
                return 'Off';
              })()}
            </span>
          </div>
          {(analysis.reportMode ?? (analysis.sendConversationReports ? 'every_conversation' : 'off')) !== 'off' &&
            analysis.supportEmails &&
            analysis.supportEmails.length > 0 && (
            <div>
              <span className="font-medium text-gray-900">Recipients:</span>
              <div className="mt-1">
                {analysis.supportEmails.map((email, i) => (
                  <Badge key={i} variant="secondary" className="mr-1 mb-1">
                    <CopyableEmail email={email} />
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </SummaryCard>

        {/* Knowledge Base Summary */}
        <SummaryCard title="Knowledge Base" genieIcon="book-open" stepId="knowledge">
          <div>
            <span className="font-medium text-gray-900">Documents:</span>
            <span className="text-gray-700 ml-2">
              {knowledgeBase && knowledgeBase.length > 0
                ? `${knowledgeBase.length} document${knowledgeBase.length > 1 ? 's' : ''} selected`
                : 'No documents selected'}
            </span>
          </div>
          {knowledgeBase && knowledgeBase.length > 0 && (
            <div className="ml-4 mt-2 space-y-1">
              {knowledgeBase.slice(0, 3).map((doc, i) => (
                <div key={i} className="text-sm text-gray-600">
                  • {doc.name || `Document ${i + 1}`}
                </div>
              ))}
              {knowledgeBase.length > 3 && (
                <div className="text-sm text-gray-500">
                  +{knowledgeBase.length - 3} more...
                </div>
              )}
            </div>
          )}
        </SummaryCard>

        {/* Advanced Summary */}
        <SummaryCard title="Advanced Settings" genieIcon="palette" stepId="advanced">
          <InfoRow
            label="Max Duration"
            value={`${analysis.conversationSettings?.maxDuration || 600} seconds`}
          />
          <InfoRow
            label="Turn Timeout"
            value={`${analysis.conversationSettings?.turnTimeout || 20} seconds`}
          />
          <InfoRow
            label="Turn Mode"
            value={analysis.conversationSettings?.turnMode || 'silence'}
          />
          {analysis.pronunciations && analysis.pronunciations.length > 0 && (
            <InfoRow
              label="Pronunciations"
              value={`${analysis.pronunciations.length} custom pronunciation${analysis.pronunciations.length > 1 ? 's' : ''}`}
            />
          )}
        </SummaryCard>

        {/* Confirmation message */}
        <div className="bg-[#4E9CFF]/10 border border-[#4E9CFF]/30 p-4 rounded-lg">
          <p className="text-sm text-[#4E9CFF] font-semibold mb-1">
            Ready to create your genie?
          </p>
          <p className="text-sm text-gray-700">
            Your genie will be created with the settings above. You can edit everything later from your dashboard.
          </p>
        </div>
      </div>
    </ReviewSectionCard>
  );
};
